// @ts-strict-ignore
import Button, { ButtonProps } from 'antd/lib/button';
import Dropdown, { DropdownProps, DropdownButtonProps } from 'antd/lib/dropdown';
import Tooltip from 'antd/lib/tooltip';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { RootState } from '../../reducers/reducers';
import { selectIsDemoModeEnabledForRelease, selectIsAppsDemoModeEnabled } from '../../selectors/selectors';
import { useUserCanPerformAction, UserPermissionScope } from '@/utils/hooks/useUserCanPerformAction';

export const DISALLOWED_ACTION_ERROR_MESSAGE =
    "You don't have permission to perform this action. Please contact your organization's admin.";

interface PermissionedActionButtonProps extends ButtonProps {
    actionType?: RunwayUserAction;
    releaseId?: string;
    overlay?: DropdownProps['overlay'];
    menu?: DropdownProps['menu'];
    trigger?: DropdownProps['trigger'];
    buttonsRender?: DropdownButtonProps['buttonsRender'];
    shouldUseCustomButtonsRender?: boolean;
    bucketId?: string;
    appId?: string;
    permissionScope?: UserPermissionScope;
    customDisallowedMessage?: string;
    allowDemoMode?: boolean;
}

const ConditionalWrapper: React.FC<{
    children: React.ReactNode;
    isReleaseDemoModeEnabled?: boolean;
    isAppsDemoModeEnabled?: boolean;
    buttonDisabledAsOverride?: boolean;
    customDisallowedMessage?: string;
}> = ({
    children,
    isReleaseDemoModeEnabled,
    isAppsDemoModeEnabled,
    buttonDisabledAsOverride,
    customDisallowedMessage,
}) => {
    if (buttonDisabledAsOverride) {
        let disabledText = null;

        if (isReleaseDemoModeEnabled) {
            disabledText = 'You are viewing this release in demo mode.';
        } else if (isAppsDemoModeEnabled) {
            disabledText = 'You are currently in demo mode.';
        } else if (customDisallowedMessage) {
            disabledText = customDisallowedMessage + ". Please contact your organization's admin."; //always advising users to contact admin.
        } else {
            disabledText = DISALLOWED_ACTION_ERROR_MESSAGE;
        }

        return (
            <Tooltip title={disabledText} placement="topRight">
                {children}
            </Tooltip>
        );
    }
    return <>{children}</>;
};

const PermissionedActionButton: React.FC<PermissionedActionButtonProps> = (props: PermissionedActionButtonProps) => {
    const {
        actionType,
        releaseId,
        children,
        disabled,
        bucketId,
        appId,
        permissionScope = UserPermissionScope.App,
        customDisallowedMessage,
        allowDemoMode,
        ...rest
    } = props;
    const userCanPerformAction = useUserCanPerformAction(
        actionType,
        releaseId,
        undefined,
        bucketId,
        allowDemoMode,
        appId,
        permissionScope,
    );
    const isReleaseDemoModeEnabled = useSelector((state: RootState) =>
        props.releaseId ? selectIsDemoModeEnabledForRelease(state, props.releaseId) : false,
    );
    const isAppsDemoModeEnabled = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));

    let buttonDisabled = disabled;
    let buttonDisabledAsOverride = false;

    if (!userCanPerformAction) {
        buttonDisabled = true;
        buttonDisabledAsOverride = true;
    }

    const isDropdown = !!props.overlay || !!props.menu;

    return (
        <ConditionalWrapper
            isReleaseDemoModeEnabled={isReleaseDemoModeEnabled}
            isAppsDemoModeEnabled={isAppsDemoModeEnabled}
            buttonDisabledAsOverride={buttonDisabledAsOverride}
            customDisallowedMessage={customDisallowedMessage}
        >
            {isDropdown ?
                <Dropdown.Button
                    disabled={buttonDisabled}
                    {...rest}
                    icon={undefined}
                    // TO-DO: properly address cases where buttonsRender may have been passed as a prop.
                    // • For such cases, currently we ignore what was passed in favor of buttonsRender as defined below,
                    //   meaning that some UIs dont work as intended (ex: loading states for the permissioned "sync" button
                    //   in StepContentMetadata.tsx).
                    // • I've abstained from simply moving this prop assignment above the {...rest} expression, because it would create
                    //   ui regressions in the several PermissionedActionButton UIs we have that do pass values to buttonsRender.
                    // • I created the shouldUseCustomButtonsRender prop as a stop-gap to enable some custom rendering I needed; it can
                    //   be removed once we fix the underlying oversight and implementations that depend on it.
                    buttonsRender={
                        props.shouldUseCustomButtonsRender && !!props.buttonsRender ?
                            props.buttonsRender
                        :   ([leftButton, rightButton]) => [
                                // Icon workaround for https://github.com/ant-design/ant-design/issues/34803

                                React.cloneElement(leftButton as React.ReactElement<any, string>, {
                                    icon: props.icon,
                                    style: { width: 'inherit' },
                                }),
                                rightButton,
                            ]
                    }
                >
                    {children}
                </Dropdown.Button>
            :   <Button disabled={buttonDisabled} {...rest}>
                    {children}
                </Button>
            }
        </ConditionalWrapper>
    );
};

export default PermissionedActionButton;
